package org.gymly.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Close
import androidx.compose.material.icons.outlined.Delete
import androidx.compose.material.icons.outlined.Edit
import androidx.compose.material.icons.outlined.Image
import androidx.compose.material.icons.outlined.Info
import androidx.compose.material.icons.outlined.Payments
import androidx.compose.material.icons.outlined.Search
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.model.AdminBillingPeriod
import org.gymly.model.AdminClient
import org.gymly.model.AdminMembershipOrder
import org.gymly.model.AdminMembershipOrderPayment
import org.gymly.model.AdminMembershipOrderPaymentDate
import org.gymly.model.AdminMembershipOrderStatus
import org.gymly.model.AdminMembershipOrderUpsert
import org.gymly.model.AdminMembershipPlanItem
import org.gymly.model.selectableForOrders
import org.gymly.model.AdminPaymentCollect
import org.gymly.model.AdminPaymentCollectTarget
import org.gymly.model.AdminPaymentDiscount
import org.gymly.model.AdminPaymentMethod
import org.gymly.model.AdminPaymentStatus
import org.gymly.model.AdminClientAccountApplyCredit
import org.gymly.model.AdminTrainer
import org.gymly.model.AdminPendingApprovalException
import org.gymly.model.PickedImagePayload
import org.gymly.theme.AdminColors
import org.gymly.theme.GymlySpace
import org.gymly.ui.components.FileUploadIconButton
import org.gymly.ui.components.AdminDateField
import org.gymly.ui.components.AdminDateFieldFilterWidth
import org.gymly.ui.components.ProofAttachmentView
import org.gymly.util.AdminDateOutputFormat
import org.gymly.util.formatMoney
import org.gymly.util.openProofFilePicker
import org.gymly.util.PickedImage
import org.gymly.util.parseFlexibleDateToIso
import org.gymly.util.gymlyDateIsoFromInstant
import org.gymly.util.formatIsoToDisplayDate
import org.gymly.util.todayIsoDate
import org.gymly.util.userMessage
import org.gymly.util.GymlyCurrency
import kotlin.math.roundToLong
import org.gymly.ui.components.VerticalScrollColumn

private data class MembershipOrderFormSubmit(
    val upsert: AdminMembershipOrderUpsert,
    val paymentLines: List<PaymentLineDraft> = emptyList(),
    val changeGivenCents: Long = 0L,
    val changeGivenUsdCents: Long = 0L,
    val walletApplyCents: Long = 0L,
    val remainingCollectCents: Long = 0L,
    /** Fecha de pago (yyyy-MM-dd); se envía como paidAt del cobro. */
    val paymentDate: String = "",
    /** Descuento en Bs de la orden nueva; null = sin descuento. */
    val discount: AdminPaymentDiscount? = null,
)

private fun membershipPaymentPaidAtIso(paymentDate: String): String {
    val day = parseFlexibleDateToIso(paymentDate)?.take(10)
        ?: paymentDate.take(10).takeIf { it.length >= 10 }
        ?: todayIsoDate()
    return "${day}T12:00:00Z"
}

private suspend fun finalizeMembershipOrderPayments(
    order: AdminMembershipOrder,
    planId: String,
    planAmountCents: Long,
    walletApplyCents: Long,
    remainingCollectCents: Long,
    lines: List<PaymentLineDraft>,
    changeGivenCents: Long,
    companyId: String?,
    trainerId: String?,
    paymentDate: String = todayIsoDate(),
    changeGivenUsdCents: Long = 0L,
    discount: AdminPaymentDiscount? = null,
) {
    if (walletApplyCents > 0) {
        AdminRepo.api.applyClientAccountCredit(
            order.clientId,
            AdminClientAccountApplyCredit(
                membershipOrderId = order.id,
                amountCents = walletApplyCents,
            ),
        )
    }
    val dueForLines = if (walletApplyCents > 0) {
        (planAmountCents - walletApplyCents).coerceAtLeast(0L)
    } else {
        remainingCollectCents.coerceAtLeast(0L)
    }
    // Con descuento se cobra lo que se paga en Bs (plan menos descuento); el backend deja la orden PAID.
    val collectCents = if (discount != null) {
        lines.filterNot { it.method.usesUsdAmount() }.sumOf { parseBulkAmountToCents(it.amountInput) }
    } else {
        dueForLines
    }
    if (lines.isNotEmpty() && (dueForLines > 0 || discount != null)) {
        collectMembershipOrderPayments(
            order = order,
            planId = planId,
            amountCents = collectCents,
            amountInput = centsToAmountInput(collectCents),
            lines = lines,
            changeGivenCents = changeGivenCents,
            companyId = companyId,
            trainerId = trainerId,
            paymentDate = paymentDate,
            changeGivenUsdCents = changeGivenUsdCents,
            discount = discount,
        )
    }
}

private suspend fun collectMembershipOrderPayments(
    order: AdminMembershipOrder,
    planId: String,
    amountCents: Long,
    amountInput: String,
    lines: List<PaymentLineDraft>,
    changeGivenCents: Long,
    companyId: String?,
    trainerId: String?,
    paymentDate: String = todayIsoDate(),
    changeGivenUsdCents: Long = 0L,
    discount: AdminPaymentDiscount? = null,
) {
    if (lines.isEmpty()) return
    val hasUsd = lines.any { line ->
        line.method.usesUsdAmount() && parseBulkAmountToCents(line.amountInput) > 0
    }
    if (amountCents <= 0L && !hasUsd) return
    val quote = runCatching { AdminRepo.api.currentExchangeRate() }.getOrNull()
    val payments = AdminRepo.api.collectPayments(
        AdminPaymentCollect(
            currency = GymlyCurrency.CODE,
            targets = listOf(
                AdminPaymentCollectTarget(
                    // Reutilizar la venta vinculada (complemento / 2.º abono).
                    orderId = order.salesOrderId,
                    clientId = order.clientId,
                    membershipOrderId = order.id,
                    amountCents = amountCents.coerceAtLeast(0L),
                    planId = planId,
                    branchId = companyId ?: order.branchId,
                    trainerId = trainerId ?: order.trainerId,
                ),
            ),
            lines = lines.map { it.toCollectLine(amountInput, quote) },
            paidAt = membershipPaymentPaidAtIso(paymentDate),
            changeGivenCents = changeGivenCents,
            changeGivenUsdCents = changeGivenUsdCents,
            discount = discount,
        ),
    )
    lines.forEachIndexed { index, line ->
        line.proof?.let { proof ->
            payments.getOrNull(index)?.let { AdminRepo.api.uploadPaymentProof(it.id, proof) }
        }
    }
}

private sealed class MembershipOrdersDialog {
    data object Create : MembershipOrdersDialog()
    data class Edit(val order: AdminMembershipOrder) : MembershipOrdersDialog()
    data class Detail(val order: AdminMembershipOrder) : MembershipOrdersDialog()
    data class Pay(val order: AdminMembershipOrder) : MembershipOrdersDialog()
    data class Delete(val order: AdminMembershipOrder) : MembershipOrdersDialog()
    data class Validate(val order: AdminMembershipOrder) : MembershipOrdersDialog()
}

private fun eurosToCents(raw: String): Long? {
    val cleaned = raw.trim().replace(" ", "").replace(",", ".")
    if (cleaned.isEmpty()) return null
    val value = cleaned.toDoubleOrNull() ?: return null
    if (value < 0) return null
    return (value * 100.0).roundToLong()
}

@Composable
fun AdminMembershipOrdersScreen() {
    val scope = rememberCoroutineScope()
    var orders by remember { mutableStateOf<List<AdminMembershipOrder>>(emptyList()) }
    var plans by remember { mutableStateOf<List<AdminMembershipPlanItem>>(emptyList()) }
    var trainers by remember { mutableStateOf<List<AdminTrainer>>(emptyList()) }
    var clients by remember { mutableStateOf<List<AdminClient>>(emptyList()) }
    var companyId by remember { mutableStateOf<String?>(null) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var successMsg by remember { mutableStateOf<String?>(null) }
    val pendingApprovals = LocalPendingApprovalNotifier.current
    var query by remember { mutableStateOf("") }
    val todayDefault = remember { todayIsoDate() }
    var dateFrom by remember { mutableStateOf(formatIsoToDisplayDate(todayDefault)) }
    var dateTo by remember { mutableStateOf(formatIsoToDisplayDate(todayDefault)) }
    var dialog by remember { mutableStateOf<MembershipOrdersDialog?>(null) }
    var saving by remember { mutableStateOf(false) }
    // Descuento de cobros que quedaron en aprobación: se conserva para volver a cobrar sin reescribirlo.
    val pendingDiscounts = remember { mutableMapOf<String, AdminPaymentDiscount>() }

    fun reload(silent: Boolean = false) {
        scope.launch {
            if (!silent) loading = true
            error = null
            try {
                if (!silent) {
                    runCatching { AdminRepo.api.cleanupOrphanMembershipPayments() }
                }
                orders = AdminRepo.api.listMembershipOrders()
                if (!silent) {
                    plans = AdminRepo.api.listMembershipPlans().selectableForOrders()
                    clients = AdminRepo.api.listClients().forClientPicker()
                    companyId = runCatching { AdminRepo.api.getCompany().id }.getOrElse {
                        AdminRepo.api.listBranches().firstOrNull()?.id
                    }
                    trainers = runCatching { AdminRepo.api.listTrainers() }.getOrElse {
                        AdminRepo.api.listWorkers()
                            .filter {
                                it.role == org.gymly.model.AdminStaffRole.TRAINER ||
                                    it.role == org.gymly.model.AdminStaffRole.ADMIN
                            }
                            .map { AdminTrainer(it.id, it.displayName, it.email, it.role.name) }
                    }
                }
            } catch (e: Exception) {
                error = e.userMessage("No se pudieron cargar las órdenes de membresía.")
            } finally {
                if (!silent) loading = false
            }
        }
    }

    /** Solo lista de órdenes (tras cobro): sin spinner ni recargar catálogos. */
    fun reloadOrdersSilent() {
        scope.launch {
            runCatching {
                orders = AdminRepo.api.listMembershipOrders()
            }.onFailure {
                // Mantener vista optimista; el usuario ya ve el resultado local.
            }
        }
    }

    fun patchOrder(updated: AdminMembershipOrder) {
        orders = orders.map { if (it.id == updated.id) updated else it }
    }

    LaunchedEffect(LocalAdminDataRevision.current) { reload() }

    val filtered = remember(orders, query, dateFrom, dateTo) {
        val q = query.trim().lowercase()
        val fromIso = parseFlexibleDateToIso(dateFrom) ?: todayDefault
        val toIso = parseFlexibleDateToIso(dateTo) ?: todayDefault
        val rangeStart = minOf(fromIso, toIso)
        val rangeEnd = maxOf(fromIso, toIso)
        orders.filter { o ->
            val matchesQuery = q.isEmpty() ||
                o.clientName.lowercase().contains(q) ||
                o.planName.lowercase().contains(q) ||
                o.statusLabel.lowercase().contains(q) ||
                o.displayNumber().contains(q)
            val matchesDate = o.matchesPaymentDateRange(rangeStart, rangeEnd)
            matchesQuery && matchesDate
        }
    }

    val listPaging = rememberAdminListPaging(
        initialSortKey = "client",
        resetKey = listOf(query, dateFrom, dateTo),
    )
    val pageItems = remember(filtered, listPaging.sort, listPaging.page) {
        filtered.adminSortedPaged(listPaging.sort, listPaging.page) { key ->
            when (key) {
                "status" -> { o -> o.status.name }
                "amount" -> { o -> o.amountCents }
                "date" -> { o -> o.orderDate }
                "plan" -> { o -> o.planName.lowercase() }
                "trainer" -> { o -> (o.trainerName ?: "").lowercase() }
                else -> { o -> o.clientName.lowercase() }
            }
        }
    }

    // KPI del rango / búsqueda visible (Desde–Hasta por fecha de pago).
    val kpiTotal = filtered.size
    val kpiPaid = filtered.count { it.status == AdminMembershipOrderStatus.PAID }
    val kpiPending = filtered.count { it.status == AdminMembershipOrderStatus.PENDING }
    val kpiCourtesy = filtered.count { it.status == AdminMembershipOrderStatus.COURTESY }

    BoxWithConstraints(
        Modifier.fillMaxSize().background(AdminColors.Surface).padding(GymlySpace.lg),
    ) {
        val compact = maxWidth < 900.dp
        VerticalScrollColumn(Modifier.fillMaxSize(),
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(
                    Icons.Outlined.Payments,
                    contentDescription = null,
                    tint = Color(0xFFE65100),
                    modifier = Modifier.size(28.dp),
                )
                Spacer(Modifier.width(10.dp))
                Text("Órdenes de membresía", fontSize = 24.sp, fontWeight = FontWeight.Bold)
            }
            Text(
                "Cobro e inscripción: comprobante de pago, monto y método. Al pagar se activa la membresía.",
                color = AdminColors.TextMuted,
                fontSize = 13.sp,
                modifier = Modifier.padding(top = 6.dp),
            )
            Text(
                "Para ver acceso y vigencia del socio usa Membresías.",
                color = Color(0xFFE65100),
                fontSize = 12.sp,
                fontWeight = FontWeight.Medium,
                modifier = Modifier.padding(top = 4.dp, bottom = 12.dp),
            )
            MembershipOrderPaymentBanner()
            Spacer(Modifier.height(12.dp))
            MembershipOrderKpiRow(kpiTotal, kpiPaid, kpiPending, kpiCourtesy, compact)
            Spacer(Modifier.height(12.dp))
            if (saving && dialog == null) {
                Text(
                    "Registrando cobro… no pulses de nuevo.",
                    color = AdminColors.Primary,
                    fontSize = 13.sp,
                    fontWeight = FontWeight.Medium,
                )
            }
            error?.let { Text(it, color = AdminColors.Error, fontSize = 13.sp) }
            successMsg?.let {
                Text(it, color = AdminColors.Primary, fontSize = 13.sp, modifier = Modifier.padding(top = 4.dp))
            }
            Spacer(Modifier.height(12.dp))
            MembershipOrdersDirectory(
                orders = pageItems,
                summaryOrders = filtered,
                totalCount = filtered.size,
                listPaging = listPaging,
                clients = clients,
                loading = loading,
                compact = compact,
                query = query,
                onQueryChange = { query = it },
                dateFrom = dateFrom,
                onDateFromChange = { dateFrom = it },
                dateTo = dateTo,
                onDateToChange = { dateTo = it },
                onCreate = {
                    // Un error de un cobro anterior no debe aparecer dentro de una orden nueva.
                    error = null
                    dialog = MembershipOrdersDialog.Create
                },
                onView = { dialog = MembershipOrdersDialog.Detail(it) },
                onEdit = { dialog = MembershipOrdersDialog.Edit(it) },
                onPay = { dialog = MembershipOrdersDialog.Pay(it) },
                onDelete = { dialog = MembershipOrdersDialog.Delete(it) },
                onValidate = { dialog = MembershipOrdersDialog.Validate(it) },
                proofValidation = rememberProofValidationAllowed() && canWriteModule(AdminModuleAccess.MEMBERSHIP_ORDERS),
            )
        }
    }

    when (val d = dialog) {
        is MembershipOrdersDialog.Detail -> MembershipOrderDetailDialog(d.order) { dialog = null }
        is MembershipOrdersDialog.Pay -> {
            val order = d.order
            val pendingUsd = order.remainingUsdCents()
            val remainingDueCents = order.remainingCents()
            val hasPriorPayment = order.paidCents > 0L ||
                order.allocatedCents > 0L ||
                order.paidUsdCents > 0L ||
                order.payments.isNotEmpty()
            val balanceSettlement = pendingUsd > 0L || hasPriorPayment
            // Bs de referencia (saldo o vacío); al saldar el monto escrito en Bs es la conversión real (sin BCV).
            val defaultAmount = when {
                remainingDueCents > 0L -> centsToAmountInput(remainingDueCents)
                else -> ""
            }
            // USD a cobrar ahora = pendiente operativo (no el precio del plan).
            val defaultUsdAmount = pendingUsd.takeIf { it > 0 }?.let { centsToAmountInput(it) }.orEmpty()
            val planCatalogUsdAmount = order.planUsdCents.takeIf { it > 0 }?.let { centsToAmountInput(it) }
            val alreadyPaidSummary = order.paidSummaryLabel()
                .takeIf { order.paidCents > 0L || order.paidUsdCents > 0L }
            val currentPendingSummary = buildList {
                if (pendingUsd > 0L) add(formatMoney(pendingUsd.toInt(), "USD"))
                if (remainingDueCents > 0L) add(formatMoney(remainingDueCents.toInt(), order.currency))
            }.joinToString(" · ").ifBlank { null }
            BulkRowPaymentDialog(
                personLabel = buildString {
                    append(order.displayNumber())
                    append(" · ")
                    append(order.clientName)
                    if (pendingUsd > 0L) {
                        append(" · pendiente ")
                        append(formatMoney(pendingUsd.toInt(), "USD"))
                    } else if (remainingDueCents > 0L && hasPriorPayment) {
                        append(" · faltan ")
                        append(formatMoney(remainingDueCents.toInt(), order.currency))
                    }
                },
                defaultAmount = defaultAmount.ifBlank { "0" },
                defaultUsdAmount = defaultUsdAmount,
                planCatalogUsdAmount = planCatalogUsdAmount,
                initialStatus = order.status,
                initialMethod = null,
                initialAmount = defaultAmount,
                initialPayerPhone = order.payerPhone.orEmpty(),
                clientId = order.clientId,
                membershipOrderId = order.id,
                initialLines = emptyList(),
                // Vacío = saldar el pendiente actual al confirmar este cobro.
                initialPendingUsd = "",
                checkExistingMobilePayments = true,
                balanceSettlement = balanceSettlement,
                alreadyPaidSummary = alreadyPaidSummary,
                currentPendingSummary = currentPendingSummary,
                initialDiscount = pendingDiscounts[order.id],
                allowDiscount = true,
                onDismiss = { if (!saving) dialog = null },
                onConfirmResult = { result ->
                    if (saving) return@BulkRowPaymentDialog
                    result.discount?.let { pendingDiscounts[order.id] = it }
                    // Cerrar al instante: el registro sigue en segundo plano (evita reintentos).
                    dialog = null
                    val prior = order
                    val optimistic = optimisticMembershipOrderAfterPay(prior, result)
                    patchOrder(optimistic)
                    scope.launch {
                        saving = true
                        error = null
                        try {
                            if (result.status == AdminMembershipOrderStatus.COURTESY) {
                                AdminRepo.api.updateMembershipOrder(
                                    order.id,
                                    AdminMembershipOrderUpsert(
                                        clientId = order.clientId,
                                        planId = order.planId,
                                        branchId = order.branchId,
                                        trainerId = order.trainerId,
                                        method = null,
                                        payerPhone = null,
                                        status = AdminMembershipOrderStatus.COURTESY,
                                        orderDate = order.orderDate,
                                        amountCents = 0L,
                                        pendingUsdCents = 0L,
                                        currency = order.currency,
                                        notes = order.notes,
                                    ),
                                )
                            } else if (result.lines.isNotEmpty() || result.walletAppliedCents > 0) {
                                val targetPendingUsd = result.pendingUsdCents
                                val targetPendingBs = result.pendingBsCents
                                val priorPendingUsd = order.pendingUsdCents
                                val priorPendingBs = order.pendingBsCents
                                // Con descuento no hay paso PENDING: el backend aplica el descuento y deja la orden PAID.
                                if (result.discount == null) {
                                    // Pendiente ($/Bs) antes del cobro: vacío = 0 para que el sync cierre la venta.
                                    AdminRepo.api.recordMembershipOrderPayment(
                                        id = order.id,
                                        status = AdminMembershipOrderStatus.PENDING,
                                        method = result.method ?: order.method,
                                        pendingUsdCents = targetPendingUsd,
                                        pendingBsCents = targetPendingBs,
                                    )
                                }
                                try {
                                    if (result.walletAppliedCents > 0) {
                                        AdminRepo.api.applyClientAccountCredit(
                                            order.clientId,
                                            AdminClientAccountApplyCredit(
                                                membershipOrderId = order.id,
                                                amountCents = result.walletAppliedCents,
                                            ),
                                        )
                                    }
                                    val payBs = result.amountCents
                                    val payUsd = result.usdCents
                                    if (result.lines.isNotEmpty() && (payBs > 0L || payUsd > 0L)) {
                                        // Bs escrito por la persona es la conversión real (sin tasa BCV).
                                        collectMembershipOrderPayments(
                                            order = order,
                                            planId = order.planId,
                                            amountCents = payBs.coerceAtLeast(0L),
                                            amountInput = centsToAmountInput(payBs.coerceAtLeast(0L)),
                                            lines = result.lines,
                                            changeGivenCents = result.changeGivenCents,
                                            companyId = companyId,
                                            trainerId = order.trainerId,
                                            changeGivenUsdCents = result.changeGivenUsdCents,
                                            discount = result.discount,
                                        )
                                    }
                                    // Con descuento el backend marca PAID al aplicarlo en el mismo cobro.
                                    if (targetPendingUsd <= 0L && targetPendingBs <= 0L && result.discount == null) {
                                        AdminRepo.api.recordMembershipOrderPayment(
                                            id = order.id,
                                            status = AdminMembershipOrderStatus.PAID,
                                            method = result.method ?: order.method,
                                            pendingUsdCents = 0L,
                                            pendingBsCents = 0L,
                                        )
                                    }
                                } catch (collectError: Exception) {
                                    if (result.discount == null && (targetPendingUsd != priorPendingUsd || targetPendingBs != priorPendingBs)) {
                                        runCatching {
                                            AdminRepo.api.recordMembershipOrderPayment(
                                                id = order.id,
                                                status = order.status,
                                                method = order.method,
                                                pendingUsdCents = priorPendingUsd,
                                                pendingBsCents = priorPendingBs,
                                            )
                                        }
                                    }
                                    throw collectError
                                }
                            }
                            successMsg = "Orden ${order.displayNumber()} · cobro registrado."
                            pendingDiscounts.remove(order.id)
                            reloadOrdersSilent()
                        } catch (e: Exception) {
                            // Revert automático: la lista no queda mentirosa.
                            patchOrder(prior)
                            if (pendingApprovals.offer(e)) {
                                // El cobro no se aplicó: la orden vuelve a su estado real y se avisa que espera aprobación.
                                successMsg = "Cobro de ${order.displayNumber()} enviado a aprobación. Se aplicará al aprobarlo."
                                error = null
                                reloadOrdersSilent()
                            } else {
                                successMsg = null
                                error = e.userMessage("No se pudo registrar el pago.")
                            }
                        } finally {
                            saving = false
                            // Siempre se sincroniza con el servidor: el estado mostrado es el guardado.
                            reloadOrdersSilent()
                        }
                    }
                },
            )
        }
        is MembershipOrdersDialog.Validate -> {
            val proof = d.order.payments.firstOrNull { it.status == AdminPaymentStatus.POR_VALIDAR }
            if (proof != null) {
                ProofValidateDialog(
                    amountLabel = "${d.order.displayNumber()} · ${d.order.clientName} · " +
                        "${formatMoney(proof.amountCents.toInt(), proof.currency)} (${proof.methodLabel})",
                    reference = proof.notes,
                    paymentDateIso = gymlyDateIsoFromInstant(proof.paidAt) ?: gymlyDateIsoFromInstant(proof.createdAt),
                    excludePaymentId = proof.id,
                    onDismiss = { dialog = null },
                    onDecide = { paid, reason ->
                        try {
                            AdminRepo.api.validatePaymentProof(proof.id, paid, reason)
                            successMsg = if (paid) {
                                "Cobro de ${d.order.displayNumber()} validado como pagado."
                            } else {
                                "Comprobante de ${d.order.displayNumber()} rechazado."
                            }
                            error = null
                        } catch (e: AdminPendingApprovalException) {
                            successMsg = e.message
                            error = null
                        }
                        reloadOrdersSilent()
                    },
                )
            }
        }
        is MembershipOrdersDialog.Delete -> AlertDialog(
            onDismissRequest = { if (!saving) dialog = null },
            title = { Text("Eliminar orden") },
            text = { Text("¿Eliminar ${d.order.displayNumber()} de ${d.order.clientName}?") },
            confirmButton = {
                Button(
                    onClick = {
                        scope.launch {
                            saving = true
                            error = null
                            try {
                                AdminRepo.api.deleteMembershipOrder(d.order.id)
                                // Actualiza KPI al instante (Total / Cobradas / Por cobrar / Cortesía).
                                orders = orders.filterNot { it.id == d.order.id }
                                dialog = null
                                successMsg = "${d.order.displayNumber()} eliminada."
                                reload(silent = true)
                            } catch (e: Exception) {
                                error = e.userMessage("No se pudo eliminar.")
                            } finally {
                                saving = false
                            }
                        }
                    },
                    enabled = !saving,
                    colors = ButtonDefaults.buttonColors(containerColor = AdminColors.Error),
                ) { Text("Eliminar") }
            },
            dismissButton = {
                TextButton(onClick = { dialog = null }, enabled = !saving) { Text("Cancelar") }
            },
        )
        is MembershipOrdersDialog.Create -> MembershipOrderFormDialog(
            title = "Nueva orden de membresía",
            serverError = error,
            initial = null,
            plans = plans,
            trainers = trainers,
            clients = clients,
            companyId = companyId,
            saving = saving,
            onDismiss = { if (!saving) dialog = null },
            onSave = { submit ->
                if (saving) return@MembershipOrderFormDialog
                scope.launch {
                    saving = true
                    error = null
                    try {
                        val created = AdminRepo.api.createMembershipOrder(submit.upsert)
                        // Cerrar tras crear la orden; el cobro sigue sin mantener el diálogo abierto.
                        dialog = null
                        if (submit.walletApplyCents > 0 || submit.paymentLines.isNotEmpty()) {
                            finalizeMembershipOrderPayments(
                                order = created,
                                planId = submit.upsert.planId,
                                planAmountCents = submit.upsert.amountCents ?: 0L,
                                walletApplyCents = submit.walletApplyCents,
                                remainingCollectCents = submit.remainingCollectCents,
                                lines = submit.paymentLines,
                                changeGivenCents = submit.changeGivenCents,
                                companyId = companyId,
                                trainerId = submit.upsert.trainerId,
                                paymentDate = submit.paymentDate,
                                changeGivenUsdCents = submit.changeGivenUsdCents,
                                discount = submit.discount,
                            )
                        }
                        successMsg = "Orden ${created.displayNumber()} registrada."
                        reload()
                    } catch (e: Exception) {
                        if (pendingApprovals.offer(e)) {
                            dialog = null
                            successMsg = null
                            error = null
                        } else {
                            error = e.userMessage("No se pudo crear la orden.")
                        }
                    } finally {
                        saving = false
                    }
                }
            },
        )
        is MembershipOrdersDialog.Edit -> PaidOrderEditDialog(
            order = d.order,
            saving = saving,
            onDismiss = { if (!saving) dialog = null },
            onSave = { paymentDateIso ->
                scope.launch {
                    saving = true
                    error = null
                    try {
                        // Fecha de cobro: se aplica a cada cobro de la orden que tenga otra fecha.
                        val paymentDates = d.order.payments
                            .filter { it.paidAt != null && it.paidAt.take(10) != paymentDateIso }
                            .map { AdminMembershipOrderPaymentDate(it.id, toPaidAtIso(paymentDateIso, it.paidAt)) }
                        if (paymentDates.isNotEmpty()) {
                            AdminRepo.api.updateMembershipOrder(
                                d.order.id,
                                AdminMembershipOrderUpsert(
                                    clientId = d.order.clientId,
                                    planId = d.order.planId,
                                    branchId = d.order.branchId,
                                    trainerId = d.order.trainerId,
                                    method = d.order.method,
                                    payerPhone = d.order.payerPhone,
                                    status = AdminMembershipOrderStatus.PAID,
                                    orderDate = d.order.orderDate,
                                    amountCents = d.order.amountCents,
                                    pendingUsdCents = 0L,
                                    pendingBsCents = 0L,
                                    currency = d.order.currency,
                                    notes = d.order.notes,
                                    paymentDates = paymentDates,
                                ),
                            )
                        }
                        dialog = null
                        reload()
                    } catch (e: Exception) {
                        if (pendingApprovals.offer(e)) {
                            dialog = null
                            successMsg = null
                            error = null
                        } else {
                            error = e.userMessage("No se pudo actualizar la orden.")
                        }
                    } finally {
                        saving = false
                    }
                }
            },
        )
        null -> Unit
    }
}

@Composable
private fun MembershipOrderPaymentBanner() {
    Row(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .background(Color(0xFFFFF3E0))
            .border(1.dp, Color(0xFFE65100).copy(alpha = 0.25f), RoundedCornerShape(12.dp))
            .padding(14.dp),
        verticalAlignment = Alignment.Top,
    ) {
        Icon(Icons.Outlined.Info, null, tint = Color(0xFFE65100), modifier = Modifier.size(20.dp))
        Spacer(Modifier.width(10.dp))
        Column {
            Text(
                "Cobro / venta",
                fontWeight = FontWeight.Bold,
                fontSize = 13.sp,
                color = Color(0xFFBF360C),
            )
            Text(
                "Cada orden es un recibo: pendiente, pagado o cortesía. No muestra si el socio puede entrar hoy.",
                color = Color(0xFFE65100),
                fontSize = 12.sp,
                modifier = Modifier.padding(top = 2.dp),
            )
        }
    }
}

@Composable
private fun MembershipOrderKpiRow(total: Int, paid: Int, pending: Int, courtesy: Int, compact: Boolean) {
    val items = listOf(
        Triple("Total órdenes", total.toString(), AdminColors.Primary),
        Triple("Cobradas", paid.toString(), Color(0xFF2E7D32)),
        Triple("Por cobrar", pending.toString(), Color(0xFFE65100)),
        Triple("Cortesía", courtesy.toString(), Color(0xFF1565C0)),
    )
    if (compact) {
        Row(horizontalArrangement = Arrangement.spacedBy(10.dp), modifier = Modifier.horizontalScroll(rememberScrollState())) {
            items.forEach { (label, value, color) ->
                key(label, value) {
                    MembershipOrderKpiCard(label, value, color, Modifier.width(140.dp))
                }
            }
        }
    } else {
        Row(horizontalArrangement = Arrangement.spacedBy(12.dp), modifier = Modifier.fillMaxWidth()) {
            items.forEach { (label, value, color) ->
                key(label, value) {
                    MembershipOrderKpiCard(label, value, color, Modifier.weight(1f))
                }
            }
        }
    }
}

@Composable
private fun MembershipOrderKpiCard(label: String, value: String, accent: Color, modifier: Modifier = Modifier) {
    Column(
        modifier
            .clip(RoundedCornerShape(14.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(16.dp),
    ) {
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
        Text(value, fontWeight = FontWeight.Bold, fontSize = 22.sp, color = accent)
    }
}

/**
 * Vista inmediata tras Cobrar: refleja pendiente Bs/$ del formulario.
 * Si falla el API se revierte a la fila anterior.
 */
private fun optimisticMembershipOrderAfterPay(
    prior: AdminMembershipOrder,
    result: BulkPaymentFormResult,
): AdminMembershipOrder {
    if (result.status == AdminMembershipOrderStatus.COURTESY) {
        return prior.copy(
            status = AdminMembershipOrderStatus.COURTESY,
            statusLabel = AdminMembershipOrderStatus.COURTESY.labelEs(),
            method = null,
            methodLabel = null,
            amountCents = 0L,
            pendingUsdCents = 0L,
            pendingBsCents = 0L,
            paidCents = 0L,
            paidUsdCents = 0L,
        )
    }
    val pendingUsd = result.pendingUsdCents.coerceAtLeast(0L)
    val pendingBs = result.pendingBsCents.coerceAtLeast(0L)
    val hasPending = pendingUsd > 0L || pendingBs > 0L
    val status = if (hasPending) {
        AdminMembershipOrderStatus.PENDING
    } else {
        AdminMembershipOrderStatus.PAID
    }
    val method = result.method ?: prior.method
    return prior.copy(
        status = status,
        statusLabel = status.labelEs(),
        method = method,
        methodLabel = method?.labelEs() ?: prior.methodLabel,
        pendingUsdCents = pendingUsd,
        pendingBsCents = pendingBs,
        paidCents = prior.paidCents + result.amountCents.coerceAtLeast(0L),
        paidUsdCents = prior.paidUsdCents + result.usdCents.coerceAtLeast(0L),
        allocatedCents = prior.allocatedCents + result.amountCents.coerceAtLeast(0L),
        discountCents = result.discount?.cents ?: prior.discountCents,
        discountReason = result.discount?.reason ?: prior.discountReason,
        discountNote = result.discount?.note ?: prior.discountNote,
    )
}

/** Días de cobro reales en zona Caracas; sin pagos usa creación / inicio plan. */
private fun AdminMembershipOrder.paymentFilterDays(): List<String> {
    if (payments.isNotEmpty()) {
        return payments.mapNotNull { pay ->
            gymlyDateIsoFromInstant(pay.paidAt) ?: gymlyDateIsoFromInstant(pay.createdAt)
        }.distinct()
    }
    return listOfNotNull(
        gymlyDateIsoFromInstant(createdAt),
        orderDate.take(10).takeIf { it.length >= 10 },
    ).distinct()
}

private fun AdminMembershipOrder.matchesPaymentDateRange(rangeStart: String, rangeEnd: String): Boolean =
    paymentFilterDays().any { day -> day >= rangeStart && day <= rangeEnd }

@Composable
private fun MembershipOrderDateRangeRow(
    dateFrom: String,
    onDateFromChange: (String) -> Unit,
    dateTo: String,
    onDateToChange: (String) -> Unit,
) {
    Row(
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        AdminDateField(
            value = dateFrom,
            onValueChange = onDateFromChange,
            modifier = Modifier.width(AdminDateFieldFilterWidth),
            label = "Desde",
            placeholder = "DD-MM-AAAA",
            outputFormat = AdminDateOutputFormat.DISPLAY,
        )
        AdminDateField(
            value = dateTo,
            onValueChange = onDateToChange,
            modifier = Modifier.width(AdminDateFieldFilterWidth),
            label = "Hasta",
            placeholder = "DD-MM-AAAA",
            outputFormat = AdminDateOutputFormat.DISPLAY,
        )
    }
}

@Composable
private fun MembershipOrdersDirectory(
    orders: List<AdminMembershipOrder>,
    summaryOrders: List<AdminMembershipOrder>,
    totalCount: Int,
    listPaging: AdminListPaging,
    clients: List<AdminClient>,
    loading: Boolean,
    compact: Boolean,
    query: String,
    onQueryChange: (String) -> Unit,
    dateFrom: String,
    onDateFromChange: (String) -> Unit,
    dateTo: String,
    onDateToChange: (String) -> Unit,
    onCreate: () -> Unit,
    onView: (AdminMembershipOrder) -> Unit,
    onEdit: (AdminMembershipOrder) -> Unit,
    onPay: (AdminMembershipOrder) -> Unit,
    onDelete: (AdminMembershipOrder) -> Unit,
    onValidate: (AdminMembershipOrder) -> Unit,
    proofValidation: Boolean,
) {
    val moneySummary = remember(summaryOrders, dateFrom, dateTo) {
        membershipOrderMoneySummary(summaryOrders, dateFrom, dateTo)
    }
    Column(
        Modifier.fillMaxWidth().clip(RoundedCornerShape(16.dp)).background(AdminColors.SurfaceContainer).padding(16.dp),
    ) {
        if (compact) {
            Text("Listado de cobros", fontWeight = FontWeight.Bold, fontSize = 16.sp)
            Spacer(Modifier.height(10.dp))
            MembershipOrderDateRangeRow(dateFrom, onDateFromChange, dateTo, onDateToChange)
            Spacer(Modifier.height(10.dp))
            OutlinedTextField(
                value = query,
                onValueChange = onQueryChange,
                modifier = Modifier.fillMaxWidth(),
                placeholder = { Text("Buscar…") },
                singleLine = true,
                leadingIcon = { Icon(Icons.Outlined.Search, null, tint = AdminColors.TextMuted) },
                shape = RoundedCornerShape(10.dp),
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = AdminColors.Primary,
                    unfocusedBorderColor = AdminColors.Outline,
                ),
            )
            Spacer(Modifier.height(10.dp))
            if (canWriteModule(AdminModuleAccess.MEMBERSHIP_ORDERS)) Button(
                onClick = onCreate,
                modifier = Modifier.fillMaxWidth(),
                shape = RoundedCornerShape(10.dp),
                colors = ButtonDefaults.buttonColors(containerColor = AdminColors.Primary, contentColor = AdminColors.OnBrand),
            ) { Text("+ Nueva orden") }
        } else {
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text("Listado de cobros", fontWeight = FontWeight.Bold, fontSize = 16.sp)
                Row(horizontalArrangement = Arrangement.spacedBy(10.dp), verticalAlignment = Alignment.Bottom) {
                    MembershipOrderDateRangeRow(dateFrom, onDateFromChange, dateTo, onDateToChange)
                    OutlinedTextField(
                        value = query,
                        onValueChange = onQueryChange,
                        modifier = Modifier.widthIn(max = 220.dp),
                        placeholder = { Text("Buscar…") },
                        singleLine = true,
                        leadingIcon = { Icon(Icons.Outlined.Search, null, tint = AdminColors.TextMuted) },
                        shape = RoundedCornerShape(10.dp),
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = AdminColors.Primary,
                            unfocusedBorderColor = AdminColors.Outline,
                        ),
                    )
                    if (canWriteModule(AdminModuleAccess.MEMBERSHIP_ORDERS)) Button(
                        onClick = onCreate,
                        shape = RoundedCornerShape(10.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = AdminColors.Primary, contentColor = AdminColors.OnBrand),
                    ) { Text("+ Nueva orden") }
                }
            }
        }
        if (!loading && moneySummary.hasContent) {
            Spacer(Modifier.height(12.dp))
            MembershipOrderMoneySummaryRow(moneySummary)
        }
        Spacer(Modifier.height(14.dp))
        when {
            loading -> Box(Modifier.fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = AdminColors.Primary)
            }
            totalCount == 0 -> Text(
                "No hay cobros en el rango de fechas seleccionado.",
                color = AdminColors.TextMuted,
            )
            else -> {
                if (!compact) {
                    MembershipOrderTableHeader(listPaging)
                    HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.35f))
                }
                orders.forEach { o ->
                    val client = clients.firstOrNull { it.id == o.clientId }
                    MembershipOrderRow(
                        order = o,
                        client = client,
                        compact = compact,
                        onView = onView,
                        onEdit = onEdit,
                        onPay = onPay,
                        onDelete = onDelete,
                        onValidate = onValidate,
                        proofValidation = proofValidation,
                    )
                    HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.35f))
                }
                AdminListPaginationBar(
                    totalItems = totalCount,
                    page = listPaging.page,
                    onPageChange = { listPaging.page = it },
                )
            }
        }
    }
}

private data class MembershipOrderMethodMoney(
    val key: String,
    val label: String,
    val bsCents: Long = 0L,
    val usdCents: Long = 0L,
)

private data class MembershipOrderMoneySummary(
    val methods: List<MembershipOrderMethodMoney> = emptyList(),
    val pendingBsCents: Long = 0L,
    val pendingUsdCents: Long = 0L,
    val changeGivenCents: Long = 0L,
    val currency: String = GymlyCurrency.CODE,
) {
    val hasContent: Boolean
        get() = methods.isNotEmpty() ||
            pendingBsCents > 0L ||
            pendingUsdCents > 0L ||
            changeGivenCents > 0L
}

private fun membershipOrderMoneySummary(
    orders: List<AdminMembershipOrder>,
    dateFrom: String,
    dateTo: String,
): MembershipOrderMoneySummary {
    val fromIso = parseFlexibleDateToIso(dateFrom)
    val toIso = parseFlexibleDateToIso(dateTo)
    val rangeStart = if (fromIso != null && toIso != null) minOf(fromIso, toIso) else fromIso
    val rangeEnd = if (fromIso != null && toIso != null) maxOf(fromIso, toIso) else toIso
    val methodTotals = linkedMapOf<String, MembershipOrderMethodMoney>()
    val changeByCollection = linkedMapOf<String, Long>()
    var pendingBs = 0L
    var pendingUsd = 0L
    var currency = GymlyCurrency.CODE

    fun addMethod(key: String, label: String, bs: Long, usd: Long) {
        if (bs <= 0L && usd <= 0L) return
        val existing = methodTotals[key]
        if (existing == null) {
            methodTotals[key] = MembershipOrderMethodMoney(key, label, bs.coerceAtLeast(0L), usd.coerceAtLeast(0L))
        } else {
            methodTotals[key] = existing.copy(
                bsCents = existing.bsCents + bs.coerceAtLeast(0L),
                usdCents = existing.usdCents + usd.coerceAtLeast(0L),
            )
        }
    }

    orders.forEach { order ->
        if (order.currency.isNotBlank()) currency = order.currency
        if (order.status == AdminMembershipOrderStatus.PENDING) {
            pendingBs += order.remainingCents()
            pendingUsd += order.remainingUsdCents()
        }
        val rangedPayments = order.payments.filter { pay ->
            val day = gymlyDateIsoFromInstant(pay.paidAt) ?: gymlyDateIsoFromInstant(pay.createdAt)
            if (day.isNullOrBlank() || rangeStart == null || rangeEnd == null) {
                true
            } else {
                day >= rangeStart && day <= rangeEnd
            }
        }
        if (rangedPayments.isNotEmpty()) {
            rangedPayments.forEach { pay ->
                if (pay.status != AdminPaymentStatus.PAID && pay.status != AdminPaymentStatus.PENDING) return@forEach
                val label = pay.methodLabel.ifBlank { pay.method?.labelEs() ?: "Otro" }
                val key = pay.method?.name ?: label.lowercase()
                addMethod(key, label, pay.amountCents, pay.foreignUsdCents ?: 0L)
                val collectionId = pay.collectionId
                if (!collectionId.isNullOrBlank() && pay.changeGivenCents > 0L) {
                    changeByCollection[collectionId] = maxOf(
                        changeByCollection[collectionId] ?: 0L,
                        pay.changeGivenCents,
                    )
                }
            }
        } else if (order.payments.isEmpty()) {
            // Cortesía / pendiente sin líneas: usa método de la orden si hubo abono.
            val label = order.methodLabel ?: order.method?.labelEs() ?: return@forEach
            val key = order.method?.name ?: label.lowercase()
            addMethod(key, label, order.paidCents, order.paidUsdCents)
        }
    }

    val preferred = AdminPaymentMethod.adminChoices().map { it.name }
    val methods = methodTotals.values
        .filter { it.bsCents > 0L || it.usdCents > 0L }
        .sortedWith(
            compareBy<MembershipOrderMethodMoney> { m ->
                val idx = preferred.indexOf(m.key)
                if (idx >= 0) idx else preferred.size
            }.thenBy { it.label.lowercase() },
        )

    return MembershipOrderMoneySummary(
        methods = methods,
        pendingBsCents = pendingBs,
        pendingUsdCents = pendingUsd,
        changeGivenCents = changeByCollection.values.sum(),
        currency = currency,
    )
}

@Composable
private fun MembershipOrderMoneySummaryRow(
    summary: MembershipOrderMoneySummary,
) {
    val chips = buildList {
        summary.methods.forEach { method ->
            val amount = buildList {
                if (method.bsCents > 0L) add(formatMoney(method.bsCents.toInt(), summary.currency))
                if (method.usdCents > 0L) add(formatMoney(method.usdCents.toInt(), "USD"))
            }.joinToString(" · ")
            add(Triple(method.label, amount, AdminColors.Primary))
        }
        if (summary.pendingBsCents > 0L || summary.pendingUsdCents > 0L) {
            val pending = buildList {
                if (summary.pendingUsdCents > 0L) {
                    add(formatMoney(summary.pendingUsdCents.toInt(), "USD"))
                }
                if (summary.pendingBsCents > 0L) {
                    add(formatMoney(summary.pendingBsCents.toInt(), summary.currency))
                }
            }.joinToString(" · ")
            add(Triple("Pendientes", pending, Color(0xFFE65100)))
        }
        if (summary.changeGivenCents > 0L) {
            add(
                Triple(
                    "Vuelto",
                    formatMoney((-summary.changeGivenCents).toInt(), summary.currency),
                    AdminColors.Error,
                ),
            )
        }
    }
    if (chips.isEmpty()) return

    Column(Modifier.fillMaxWidth()) {
        Text(
            "Totales del rango",
            fontWeight = FontWeight.SemiBold,
            fontSize = 12.sp,
            color = AdminColors.TextMuted,
        )
        Spacer(Modifier.height(8.dp))
        Row(
            modifier = Modifier.fillMaxWidth().horizontalScroll(rememberScrollState()),
            horizontalArrangement = Arrangement.spacedBy(10.dp),
        ) {
            chips.forEach { (label, value, color) ->
                key(label, value) {
                    Column(
                        Modifier
                            .widthIn(min = 120.dp)
                            .clip(RoundedCornerShape(12.dp))
                            .border(1.dp, AdminColors.Outline.copy(alpha = 0.45f), RoundedCornerShape(12.dp))
                            .background(AdminColors.Surface)
                            .padding(horizontal = 12.dp, vertical = 10.dp),
                    ) {
                        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
                        Text(value, fontWeight = FontWeight.Bold, fontSize = 14.sp, color = color)
                    }
                }
            }
        }
    }
}

@Composable
private fun MembershipOrderTableHeader(listPaging: AdminListPaging) {
    Row(
        Modifier
            .fillMaxWidth()
            .padding(vertical = 8.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(4.dp),
    ) {
        Spacer(Modifier.width(12.dp))
        AdminSortableHeader("Cliente", "client", listPaging.sort, listPaging::onSort, Modifier.weight(1.4f))
        AdminSortableHeader("Plan", "plan", listPaging.sort, listPaging::onSort, Modifier.weight(1f))
        AdminSortableHeader("Entrenador", "trainer", listPaging.sort, listPaging::onSort, Modifier.weight(1f))
        AdminSortableHeader("Inicio plan", "date", listPaging.sort, listPaging::onSort, Modifier.weight(0.85f))
        AdminListHeaderLabel("Vencimiento", Modifier.weight(0.85f))
        AdminSortableHeader("Estado", "status", listPaging.sort, listPaging::onSort, Modifier.weight(0.85f))
        AdminSortableHeader("Monto", "amount", listPaging.sort, listPaging::onSort, Modifier.weight(0.85f))
        AdminListHeaderLabel("Acciones", Modifier.width(240.dp))
    }
}

@Composable
private fun MembershipOrderRow(
    order: AdminMembershipOrder,
    client: AdminClient?,
    compact: Boolean,
    onView: (AdminMembershipOrder) -> Unit,
    onEdit: (AdminMembershipOrder) -> Unit,
    onPay: (AdminMembershipOrder) -> Unit,
    onDelete: (AdminMembershipOrder) -> Unit,
    onValidate: (AdminMembershipOrder) -> Unit,
    proofValidation: Boolean,
) {
    val isPending = order.status == AdminMembershipOrderStatus.PENDING
    // Cobro con comprobante "Por validar": el botón de validar solo se habilita si existe.
    val proofPayment = order.payments.firstOrNull { it.status == AdminPaymentStatus.POR_VALIDAR }
    val statusText = if (proofPayment != null) AdminPaymentStatus.POR_VALIDAR.labelEs() else order.statusLabel
    val contactLine = listOfNotNull(
        client?.email,
        client?.docId?.let { "Céd. $it" },
        client?.phone,
    ).joinToString(" · ").ifBlank { null }

    if (compact) {
        Column(
            Modifier
                .fillMaxWidth()
                .padding(vertical = 12.dp),
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Box(Modifier.size(8.dp).clip(CircleShape).background(Color(order.status.dotColorArgb())))
                Spacer(Modifier.width(8.dp))
                Column(Modifier.weight(1f)) {
                    Text("${order.displayNumber()} · ${order.clientName}", fontWeight = FontWeight.Bold, fontSize = 14.sp)
                    contactLine?.let {
                        Text(it, color = AdminColors.TextMuted, fontSize = 12.sp)
                    }
                }
                if (isPending) {
                    Button(
                        onClick = { onPay(order) },
                        shape = RoundedCornerShape(8.dp),
                        contentPadding = androidx.compose.foundation.layout.PaddingValues(horizontal = 12.dp, vertical = 6.dp),
                        enabled = canWriteModule(AdminModuleAccess.PAYMENTS),
                        colors = ButtonDefaults.buttonColors(
                            containerColor = AdminColors.Primary,
                            contentColor = AdminColors.OnBrand,
                        ),
                    ) {
                        Icon(Icons.Outlined.Payments, null, Modifier.size(16.dp))
                        Spacer(Modifier.width(4.dp))
                        Text("Pagar", fontSize = 12.sp, fontWeight = FontWeight.SemiBold)
                    }
                }
            }
            Spacer(Modifier.height(8.dp))
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                OrderInfoChip("Plan", order.planName)
                OrderInfoChip("Entrenador", order.trainerName ?: "Sin entrenador")
                OrderInfoChip("Inicio plan", order.orderDate)
                OrderInfoChip("Vencimiento", order.planEndsAt ?: "—")
            }
            Spacer(Modifier.height(6.dp))
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                Text(
                    buildString {
                        append(statusText)
                        append(" · ")
                        append(formatMoney(order.amountCents.toInt(), order.currency))
                        if (order.discountCents > 0L) {
                            append(" · descuento ")
                            append(formatMoney(order.discountCents.toInt(), order.currency))
                        }
                        if (isPending && order.remainingUsdCents() > 0L) {
                            append(" · pendiente ")
                            append(formatMoney(order.remainingUsdCents().toInt(), "USD"))
                            val remBs = order.remainingCents()
                            if (remBs > 0L && remBs < order.amountCents) {
                                append(" · por cobrar ")
                                append(formatMoney(remBs.toInt(), order.currency))
                            }
                        }
                    },
                    color = if (isPending) Color(0xFFE65100) else AdminColors.Primary,
                    fontSize = 13.sp,
                    fontWeight = FontWeight.SemiBold,
                )
            }
            MembershipOrderPaymentsSummary(order)
            Spacer(Modifier.height(8.dp))
            Row(horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                if (proofValidation) OutlinedButton(
                    onClick = { onValidate(order) },
                    enabled = proofPayment != null,
                    shape = RoundedCornerShape(8.dp),
                ) { Text("Validar") }
                OutlinedButton(onClick = { onView(order) }, shape = RoundedCornerShape(8.dp)) { Text("Ver") }
                // Igual que Ventas: solo la orden pagada se edita (entrenador y fechas de cobro); lo pendiente se cobra con Pagar.
                if (order.status == AdminMembershipOrderStatus.PAID) {
                    if (canWriteModule(AdminModuleAccess.MEMBERSHIP_ORDERS)) OutlinedButton(onClick = { onEdit(order) }, shape = RoundedCornerShape(8.dp)) { Text("Editar") }
                }
                if (canDeleteModule(AdminModuleAccess.MEMBERSHIP_ORDERS)) OutlinedButton(
                    onClick = { onDelete(order) },
                    shape = RoundedCornerShape(8.dp),
                    colors = ButtonDefaults.outlinedButtonColors(contentColor = AdminColors.Error),
                ) { Text("Eliminar") }
            }
        }
        return
    }

    Column(Modifier.fillMaxWidth().padding(vertical = 8.dp)) {
        Row(
            Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(4.dp),
        ) {
            Box(Modifier.size(8.dp).clip(CircleShape).background(Color(order.status.dotColorArgb())))
            Spacer(Modifier.width(4.dp))
            Column(Modifier.weight(1.4f)) {
                Text("${order.displayNumber()} · ${order.clientName}", fontWeight = FontWeight.Bold, fontSize = 14.sp, maxLines = 1)
                contactLine?.let {
                    Text(it, color = AdminColors.TextMuted, fontSize = 12.sp, modifier = Modifier.padding(top = 2.dp), maxLines = 1)
                }
            }
            Text(
                order.planName,
                Modifier.weight(1f),
                fontSize = 13.sp,
                color = AdminColors.Text,
                maxLines = 2,
            )
            Text(
                order.trainerName ?: "Sin entrenador",
                Modifier.weight(1f),
                fontSize = 13.sp,
                color = AdminColors.TextMuted,
                maxLines = 2,
            )
            Text(
                order.orderDate,
                Modifier.weight(0.85f),
                fontSize = 13.sp,
                color = AdminColors.TextMuted,
                maxLines = 1,
            )
            Text(
                order.planEndsAt ?: "—",
                Modifier.weight(0.85f),
                fontSize = 13.sp,
                color = AdminColors.TextMuted,
                maxLines = 1,
            )
            Text(
                statusText,
                Modifier.weight(0.85f),
                fontSize = 13.sp,
                fontWeight = FontWeight.SemiBold,
                maxLines = 1,
                color = if (proofPayment != null) Color(0xFF1565C0) else when (order.status) {
                    AdminMembershipOrderStatus.PENDING -> Color(0xFFE65100)
                    AdminMembershipOrderStatus.PAID -> Color(0xFF2E7D32)
                    AdminMembershipOrderStatus.COURTESY -> Color(0xFF1565C0)
                },
            )
            Column(Modifier.weight(0.85f)) {
                Text(
                    formatMoney(order.amountCents.toInt(), order.currency),
                    fontSize = 13.sp,
                    fontWeight = FontWeight.Medium,
                    color = AdminColors.Text,
                    maxLines = 1,
                )
                // Vuelto del cobro en Bs (siempre en bolívares).
                if (order.changeGivenCents > 0L) {
                    Text(
                        "Vuelto ${formatMoney(order.changeGivenCents.toInt(), GymlyCurrency.CODE)}",
                        fontSize = 12.sp,
                        color = Color(0xFF7C3AED),
                        maxLines = 1,
                    )
                }
                if (order.status == AdminMembershipOrderStatus.PENDING && order.remainingUsdCents() > 0L) {
                    Text(
                        "Pendiente ${formatMoney(order.remainingUsdCents().toInt(), "USD")}",
                        fontSize = 12.sp,
                        color = Color(0xFFE65100),
                        maxLines = 1,
                    )
                    val remBs = order.remainingCents()
                    if (remBs > 0L && remBs < order.amountCents) {
                        Text(
                            "Por cobrar ${formatMoney(remBs.toInt(), order.currency)}",
                            fontSize = 12.sp,
                            color = Color(0xFFE65100),
                            maxLines = 1,
                        )
                    }
                } else if (order.status == AdminMembershipOrderStatus.PENDING && order.remainingCents() < order.amountCents) {
                    if (order.pendingApprovalCents() > 0L) {
                        Text(
                            "En aprobación ${formatMoney(order.pendingApprovalCents().toInt(), order.currency)}",
                            fontSize = 12.sp,
                            color = Color(0xFF1565C0),
                        )
                    }
                    if (order.remainingCents() > 0L) {
                        Text(
                            "Faltan ${formatMoney(order.remainingCents().toInt(), order.currency)}",
                            fontSize = 12.sp,
                            color = Color(0xFFE65100),
                        )
                    }
                }
            }
            Row(
                horizontalArrangement = Arrangement.spacedBy(4.dp),
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier.width(240.dp),
            ) {
                if (isPending) {
                    Button(
                        onClick = { onPay(order) },
                        shape = RoundedCornerShape(8.dp),
                        contentPadding = androidx.compose.foundation.layout.PaddingValues(horizontal = 10.dp, vertical = 6.dp),
                        enabled = canWriteModule(AdminModuleAccess.PAYMENTS),
                        colors = ButtonDefaults.buttonColors(
                            containerColor = AdminColors.Primary,
                            contentColor = AdminColors.OnBrand,
                        ),
                    ) {
                        Icon(Icons.Outlined.Payments, null, Modifier.size(15.dp))
                        Spacer(Modifier.width(4.dp))
                        Text("Pagar", fontSize = 12.sp, fontWeight = FontWeight.SemiBold)
                    }
                }
                if (proofValidation) {
                    ProofValidateButton(enabled = proofPayment != null) { onValidate(order) }
                }
                IconButton(onClick = { onView(order) }) {
                    Icon(Icons.Outlined.Search, "Ver", tint = AdminColors.TextMuted)
                }
                if (order.status == AdminMembershipOrderStatus.PAID) {
                    if (canWriteModule(AdminModuleAccess.MEMBERSHIP_ORDERS)) IconButton(onClick = { onEdit(order) }) {
                        Icon(Icons.Outlined.Edit, "Editar", tint = AdminColors.TextMuted)
                    }
                }
                if (canDeleteModule(AdminModuleAccess.MEMBERSHIP_ORDERS)) IconButton(onClick = { onDelete(order) }) {
                    Icon(Icons.Outlined.Delete, "Eliminar", tint = AdminColors.Error)
                }
            }
        }
        MembershipOrderPaymentsSummary(order, Modifier.padding(start = 16.dp, top = 4.dp, end = 8.dp))
    }
}

@Composable
private fun MembershipOrderPaymentsSummary(order: AdminMembershipOrder, modifier: Modifier = Modifier) {
    if (order.payments.isEmpty()) {
        order.methodsSummary()?.let {
            Text(it, modifier = modifier, fontSize = 12.sp, color = AdminColors.TextMuted)
        }
        return
    }
    Column(modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(2.dp)) {
        val visiblePayments = order.payments.filter { pay ->
            pay.amountCents > 0L || (pay.foreignUsdCents ?: 0L) > 0L
        }
        visiblePayments.forEachIndexed { index, pay ->
            Text(
                buildString {
                    append("Cobro ${index + 1}/${visiblePayments.size}: ")
                    append(pay.methodLabel)
                    append(" · ")
                    append(
                        buildList {
                            if (pay.amountCents > 0) {
                                add(formatMoney(pay.amountCents.toInt(), pay.currency))
                            }
                            pay.foreignUsdCents?.takeIf { it > 0 }?.let {
                                add(formatMoney(it.toInt(), "USD"))
                            }
                        }.joinToString(" · ").ifBlank {
                            formatMoney(0, pay.currency)
                        },
                    )
                    append(" · ")
                    append(pay.operationDateTime())
                    if (pay.statusLabel.isNotBlank()) {
                        append(" · ")
                        append(pay.statusLabel)
                    }
                },
                fontSize = 12.sp,
                color = AdminColors.TextMuted,
            )
        }
        if (order.discountCents > 0L) {
            Text(
                buildString {
                    append("Descuento ")
                    append(formatMoney(order.discountCents.toInt(), order.currency))
                    order.discountReason?.let { append(" · ${discountReasonLabel(it)}") }
                    order.discountNote?.takeIf { it.isNotBlank() }?.let { append(" · $it") }
                },
                fontSize = 12.sp,
                color = AdminColors.Primary,
                fontWeight = FontWeight.Medium,
            )
        }
    }
}

@Composable
private fun OrderInfoChip(label: String, value: String) {
    Column {
        Text(label, color = AdminColors.TextMuted, fontSize = 10.sp, fontWeight = FontWeight.Medium)
        Text(value, color = AdminColors.Text, fontSize = 12.sp, fontWeight = FontWeight.SemiBold, maxLines = 1)
    }
}

private fun discountReasonLabel(code: String): String = when (code) {
    "PRONTO_PAGO" -> "Pronto pago"
    "PROMOCION" -> "Promoción"
    "EXCEPCION" -> "Excepción acordada"
    "ERROR_PRECIO" -> "Error de precio"
    "OTRO" -> "Otro"
    else -> code
}

/** Orden pagada: solo la fecha de cobro. Monto, plan, entrenador y periodo no se editan. */
@Composable
private fun PaidOrderEditDialog(
    order: AdminMembershipOrder,
    saving: Boolean,
    onDismiss: () -> Unit,
    onSave: (paymentDateIso: String) -> Unit,
) {
    val lastPaidAt = order.payments.lastOrNull { it.paidAt != null }?.paidAt
    var paymentDate by remember(order) { mutableStateOf(lastPaidAt?.take(10).orEmpty()) }
    var formError by remember { mutableStateOf<String?>(null) }
    Dialog(onDismissRequest = { if (!saving) onDismiss() }) {
        Column(
            Modifier
                .widthIn(max = 480.dp)
                .fillMaxWidth(0.92f)
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(20.dp)
                .verticalScroll(rememberScrollState()),
        ) {
            Text("Editar orden ${order.displayNumber()}", fontWeight = FontWeight.Bold, fontSize = 18.sp)
            Text(
                "Orden pagada: solo puedes cambiar la fecha de cobro. Monto, plan, entrenador y periodo no se editan.",
                color = AdminColors.TextMuted,
                fontSize = 12.sp,
                modifier = Modifier.padding(top = 4.dp, bottom = 12.dp),
            )
            Text("Fecha de cobro", color = AdminColors.TextMuted, fontSize = 12.sp)
            AdminDateField(
                value = paymentDate,
                onValueChange = { paymentDate = it },
                enabled = !saving,
                placeholder = "AAAA-MM-DD",
                outputFormat = AdminDateOutputFormat.ISO,
            )
            formError?.let {
                Spacer(Modifier.height(8.dp))
                Text(it, color = AdminColors.Error, fontSize = 13.sp)
            }
            Spacer(Modifier.height(16.dp))
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                TextButton(onClick = onDismiss, enabled = !saving) {
                    Text("Cancelar", color = AdminColors.TextMuted)
                }
                Spacer(Modifier.width(8.dp))
                Button(
                    onClick = {
                        if (paymentDate.length != 10) {
                            formError = "Indica la fecha de cobro como AAAA-MM-DD."
                            return@Button
                        }
                        formError = null
                        onSave(paymentDate)
                    },
                    enabled = !saving,
                    shape = RoundedCornerShape(10.dp),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text(if (saving) "…" else "Guardar", fontWeight = FontWeight.SemiBold)
                }
            }
        }
    }
}

/** Nueva fecha (AAAA-MM-DD) con la misma hora UTC que tenía el cobro (formato ISO del servidor). */
private fun toPaidAtIso(dateIso: String, currentPaidAt: String?): String {
    val time = currentPaidAt
        ?.indexOf('T')
        ?.takeIf { it >= 0 }
        ?.let { currentPaidAt.substring(it + 1) }
        ?.takeIf { it.isNotBlank() }
        ?: "12:00:00Z"
    return "${dateIso}T" + if (time.endsWith("Z")) time else "${time}Z"
}

@Composable
private fun MembershipOrderDetailDialog(order: AdminMembershipOrder, onDismiss: () -> Unit) {
    Dialog(onDismissRequest = onDismiss) {
        Column(
            Modifier
                .widthIn(max = 640.dp)
                .fillMaxWidth()
                .heightIn(max = 680.dp)
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(20.dp)
                .verticalScroll(rememberScrollState()),
        ) {
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                Text("Orden ${order.displayNumber()}", fontWeight = FontWeight.Bold, fontSize = 18.sp)
                IconButton(onClick = onDismiss) { Icon(Icons.Outlined.Close, "Cerrar") }
            }
            CamposDetalle(detallesOrden(order))
            order.notes?.takeIf { it.isNotBlank() }?.let { DetailLine("Notas", it) }

            val visiblePayments = order.payments.filter { pay ->
                pay.amountCents > 0L || (pay.foreignUsdCents ?: 0L) > 0L
            }
            if (visiblePayments.isNotEmpty()) {
                Spacer(Modifier.height(8.dp))
                Text("Cobros (${visiblePayments.size})", fontWeight = FontWeight.Bold, fontSize = 15.sp)
                Text(
                    "Cada operación se lista por separado (método, monto y momento).",
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                    modifier = Modifier.padding(bottom = 8.dp),
                )
                visiblePayments.forEachIndexed { index, pay ->
                    MembershipOrderPaymentCard(
                        index = index + 1,
                        total = visiblePayments.size,
                        payment = pay,
                    )
                    Spacer(Modifier.height(8.dp))
                }
            } else {
                order.methodLabel?.let { DetailLine("Método", it) }
            }

            if (!order.proofUrl.isNullOrBlank()) {
                Spacer(Modifier.height(8.dp))
                ProofAttachmentView(
                    proofUrl = order.proofUrl,
                    proofContentType = order.proofContentType,
                    accent = AdminColors.Primary,
                    muted = AdminColors.TextMuted,
                    surface = AdminColors.Surface,
                    text = AdminColors.Text,
                    outline = AdminColors.Outline,
                )
            }
            Spacer(Modifier.height(12.dp))
            Button(onClick = onDismiss, modifier = Modifier.align(Alignment.End)) { Text("Cerrar") }
        }
    }
}

/** Solo los conceptos que aplican a la orden, en orden: precio, descuento, vuelto, pagado y pendientes. */
private fun detallesOrden(order: AdminMembershipOrder): List<Pair<String, String>> = buildList {
    add("Cliente" to order.clientName)
    add("Plan" to order.planName)
    add("Estado" to order.statusLabel)
    add("Inicio plan" to order.orderDate)
    add("Vencimiento" to (order.planEndsAt ?: "—"))
    order.trainerName?.let { add("Entrenador" to it) }
    add("Precio" to order.amountsSummaryLabel())
    // Un solo descuento: el escrito en $ si existe; si no, el escrito en Bs.
    if (order.discountUsdCents <= 0L && order.discountCents > 0L) {
        add(
            "Descuento (Bs)" to (
                formatMoney(order.discountCents.toInt(), order.currency) +
                    (order.discountReason?.let { " · ${discountReasonLabel(it)}" } ?: "") +
                    (order.discountNote?.takeIf { it.isNotBlank() }?.let { " · $it" } ?: "")
                ),
        )
    }
    if (order.discountUsdCents > 0L) {
        add("Descuento (\$)" to formatMoney(order.discountUsdCents.toInt(), "USD"))
    }
    if (order.changeGivenCents > 0L) {
        add("Vuelto (Bs)" to formatMoney(order.changeGivenCents.toInt(), order.currency))
    }
    if (order.paidCents > 0L || order.paidUsdCents > 0L) {
        add("Pagado" to order.paidSummaryLabel())
    }
    if (order.pendingApprovalCents() > 0L) {
        add("En aprobación" to formatMoney(order.pendingApprovalCents().toInt(), order.currency))
    }
    val pendienteBs = order.remainingCents()
    if (pendienteBs > 0L) add("Pendiente (Bs)" to formatMoney(pendienteBs.toInt(), order.currency))
    val pendienteUsd = order.remainingUsdCents()
    if (pendienteUsd > 0L) add("Pendiente (\$)" to formatMoney(pendienteUsd.toInt(), "USD"))
}

@Composable
private fun MembershipOrderPaymentCard(
    index: Int,
    total: Int,
    payment: AdminMembershipOrderPayment,
) {
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .background(AdminColors.Surface)
            .padding(12.dp),
    ) {
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
            Text(
                "Cobro $index de $total",
                fontWeight = FontWeight.SemiBold,
                fontSize = 13.sp,
            )
            Text(
                payment.statusLabel,
                color = Color(payment.status.badgeFgArgb()),
                fontSize = 12.sp,
                fontWeight = FontWeight.SemiBold,
            )
        }
        Spacer(Modifier.height(6.dp))
        val montoCobro = buildList {
            if (payment.amountCents > 0) add(formatMoney(payment.amountCents.toInt(), payment.currency))
            payment.foreignUsdCents?.takeIf { it > 0 }?.let { add(formatMoney(it.toInt(), "USD")) }
        }.joinToString(" · ").ifBlank { formatMoney(0, payment.currency) }
        CamposDetalle(
            buildList {
                add("Método" to payment.methodLabel)
                add("Monto" to montoCobro)
                add("Fecha/hora" to payment.operationDateTime())
                payment.payerPhone?.takeIf { it.isNotBlank() }?.let { add("Tel. pagador" to it) }
            },
        )
        // La referencia solo aplica al pago móvil.
        if (payment.method == AdminPaymentMethod.MOBILE_PAYMENT) {
            payment.notes?.takeIf { it.isNotBlank() }?.let { DetailLine("Referencia", it) }
        }
        if (!payment.proofUrl.isNullOrBlank()) {
            Spacer(Modifier.height(4.dp))
            ProofAttachmentView(
                proofUrl = payment.proofUrl,
                proofContentType = payment.proofContentType,
                accent = AdminColors.Primary,
                muted = AdminColors.TextMuted,
                surface = AdminColors.SurfaceContainer,
                text = AdminColors.Text,
                outline = AdminColors.Outline,
            )
        }
    }
}


@Composable
private fun DetailLine(label: String, value: String) {
    Column(Modifier.padding(bottom = 8.dp)) {
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
        Text(value, fontSize = 14.sp, fontWeight = FontWeight.Medium)
    }
}

/** Escritorio: dos columnas iguales. Celular ([narrow]): una debajo de la otra. */
@Composable
private fun MembershipOrderFormDialog(
    title: String,
    initial: AdminMembershipOrder?,
    plans: List<AdminMembershipPlanItem>,
    trainers: List<AdminTrainer>,
    clients: List<AdminClient>,
    companyId: String?,
    saving: Boolean,
    /** Error del servidor al guardar: se muestra dentro del formulario, no detrás del diálogo. */
    serverError: String? = null,
    onDismiss: () -> Unit,
    onSave: (MembershipOrderFormSubmit) -> Unit,
) {
    var clientId by remember(initial) { mutableStateOf(initial?.clientId.orEmpty()) }
    var clientQuery by remember { mutableStateOf(initial?.clientName.orEmpty()) }
    var planId by remember(initial) { mutableStateOf(initial?.planId.orEmpty()) }
    var trainerId by remember(initial) { mutableStateOf(initial?.trainerId.orEmpty()) }
    var paymentLines by remember { mutableStateOf<List<PaymentLineDraft>>(emptyList()) }
    var changeGivenCents by remember { mutableStateOf(0L) }
    var changeGivenUsdCents by remember { mutableStateOf(0L) }
    var creditAppliedCents by remember { mutableStateOf(0L) }
    var walletBalanceCents by remember { mutableStateOf(0L) }
    var pendingUsdInput by remember(initial) {
        mutableStateOf(initial?.pendingUsdCents?.takeIf { it > 0 }?.let { centsToAmountInput(it) } ?: AMOUNT_INPUT_ZERO)
    }
    var pendingBsInput by remember(initial) {
        mutableStateOf(initial?.pendingBsCents?.takeIf { it > 0 }?.let { centsToAmountInput(it) } ?: AMOUNT_INPUT_ZERO)
    }
    var discountBsInput by remember { mutableStateOf("") }
    var discountUsdInput by remember { mutableStateOf("") }
    var discountReason by remember { mutableStateOf("") }
    var discountNote by remember { mutableStateOf("") }
    var validatePayment by remember { mutableStateOf<(() -> List<PaymentLineDraft>?)?>(null) }
    var paymentDate by remember(initial) {
        mutableStateOf(
            initial?.payments?.mapNotNull {
                gymlyDateIsoFromInstant(it.paidAt) ?: gymlyDateIsoFromInstant(it.createdAt)
            }?.firstOrNull()
                ?: todayIsoDate(),
        )
    }
    var orderDate by remember(initial) { mutableStateOf(initial?.orderDate ?: todayIsoDate()) }
    var endsAt by remember(initial) { mutableStateOf(initial?.planEndsAt.orEmpty()) }
    var formError by remember { mutableStateOf<String?>(null) }
    var periodDuplicateWarning by remember { mutableStateOf<String?>(null) }
    var clientMenu by remember { mutableStateOf(false) }
    var planMenu by remember { mutableStateOf(false) }
    var trainerMenu by remember { mutableStateOf(false) }
    val formScope = rememberCoroutineScope()

    val orderPlans = remember(plans) { plans.selectableForOrders() }
    val selectedPlan = orderPlans.firstOrNull { it.id == planId }
    // Cortesía = plan de cortesía (sin cobro); ya no hay casilla.
    val isCourtesy = selectedPlan?.billingPeriod == AdminBillingPeriod.COURTESY
        ?: plans.firstOrNull { it.id == planId }
    val startsIso = orderDate.ifBlank { bulkFormTodayIsoDate() }
    val endsIso = endsAt.ifBlank {
        selectedPlan?.let { planEndsAtFromStart(startsIso, it.billingPeriod) }.orEmpty()
    }
    // Primera carga / sin vencimiento: rellenar según plan + inicio.
    LaunchedEffect(Unit) {
        if (endsAt.isBlank() && selectedPlan != null) {
            endsAt = planEndsAtFromStart(startsIso, selectedPlan.billingPeriod).orEmpty()
        }
    }
    val planAmountInput = remember(selectedPlan) {
        selectedPlan?.let { centsToAmountInput(it.priceCents) }.orEmpty()
    }
    // $ del catálogo del plan (no conversión BCV).
    val planUsdInput = remember(selectedPlan) {
        selectedPlan?.priceUsdCents?.takeIf { it > 0 }?.let { centsToAmountInput(it) }.orEmpty()
    }
    val amountDueCents = remember(selectedPlan, creditAppliedCents) {
        ((selectedPlan?.priceCents ?: 0L) - creditAppliedCents).coerceAtLeast(0L)
    }
    val amountDueInput = remember(amountDueCents) { centsToAmountInput(amountDueCents) }
    val deferWalletApply = initial?.id == null

    LaunchedEffect(clientId, planId) {
        creditAppliedCents = 0
        walletBalanceCents = 0
    }
    // Aviso al elegir cliente + plan + fecha (antes de cobrar).
    LaunchedEffect(clientId, planId, startsIso, selectedPlan?.billingPeriod) {
        periodDuplicateWarning = null
        if (clientId.isBlank() || planId.isBlank()) return@LaunchedEffect
        val date = parseFlexibleDateToIso(startsIso)?.take(10) ?: return@LaunchedEffect
        val covered = runCatching {
            AdminRepo.api.isMembershipPeriodCovered(
                clientId = clientId,
                planId = planId,
                orderDate = date,
                excludeOrderId = initial?.id,
            )
        }.getOrDefault(false)
        if (covered) {
            periodDuplicateWarning = selectedPlan?.let {
                duplicateMembershipPeriodMessage(it.name, it.billingPeriod)
            } ?: "Este socio ya tiene ese plan para el mismo periodo."
        }
    }
    val filteredClients = remember(clients, clientQuery) {
        filterClientsForPickerSearch(clients, clientQuery, limit = 8)
    }
    val showClientBrowse = clientMenu && clientQuery.trim().isEmpty() && filteredClients.isNotEmpty()
    val showClientResults = clientMenu && clientQuery.trim().length >= 2 && filteredClients.isNotEmpty()
    val showClientEmpty = clientMenu && clientQuery.trim().length >= 2 && filteredClients.isEmpty()

    Dialog(onDismissRequest = { if (!saving) onDismiss() }) {
        // Column (no VerticalScrollColumn, que ocupa todo el alto): el diálogo mide lo que contiene.
        Column(
            Modifier
                .widthIn(max = 720.dp)
                .fillMaxWidth(0.96f)
                .heightIn(max = 620.dp)
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(16.dp)
                .verticalScroll(rememberScrollState()),
        ) {
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                Text(title, fontWeight = FontWeight.Bold, fontSize = 18.sp)
                IconButton(onClick = onDismiss, enabled = !saving) { Icon(Icons.Outlined.Close, "Cerrar") }
            }
            Spacer(Modifier.height(10.dp))

            Text("Cliente *", color = AdminColors.TextMuted, fontSize = 12.sp)
            Text(
                "Solo socios (clientes activos). El personal no aparece en este listado.",
                color = AdminColors.TextMuted,
                fontSize = 12.sp,
                modifier = Modifier.padding(bottom = 4.dp),
            )
            OutlinedTextField(
                value = clientQuery,
                onValueChange = {
                    clientQuery = it
                    clientId = ""
                    clientMenu = true
                },
                modifier = Modifier.fillMaxWidth(),
                enabled = !saving,
                placeholder = { Text("Busca por nombre, correo o cédula") },
                singleLine = true,
                shape = RoundedCornerShape(10.dp),
            )
            if (showClientBrowse || showClientResults) {
                Column(
                    Modifier
                        .fillMaxWidth()
                        .padding(top = 4.dp)
                        .clip(RoundedCornerShape(10.dp))
                        .border(1.dp, AdminColors.Outline.copy(alpha = 0.7f), RoundedCornerShape(10.dp))
                        .background(AdminColors.Surface),
                ) {
                    filteredClients.forEachIndexed { index, c ->
                        Text(
                            "${c.fullName} · ${c.email ?: c.docId ?: "—"}",
                            modifier = Modifier
                                .fillMaxWidth()
                                .clickable {
                                    clientId = c.id
                                    clientQuery = c.fullName
                                    clientMenu = false
                                }
                                .padding(horizontal = 12.dp, vertical = 10.dp),
                            color = AdminColors.Text,
                            fontSize = 13.sp,
                        )
                        if (index < filteredClients.lastIndex) {
                            HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.35f))
                        }
                    }
                }
            } else if (showClientEmpty) {
                Text(
                    "Sin socios coincidentes. Escribe al menos 2 caracteres.",
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                    modifier = Modifier.padding(top = 4.dp),
                )
            }

            Spacer(Modifier.height(12.dp))
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(12.dp),
                verticalAlignment = Alignment.Top,
            ) {
                Column(Modifier.weight(1f)) {
                    Text("Plan *", color = AdminColors.TextMuted, fontSize = 12.sp)
                    Box {
                        OutlinedButton(
                            onClick = { planMenu = true },
                            modifier = Modifier.fillMaxWidth(),
                            shape = RoundedCornerShape(10.dp),
                        ) {
                            Text(selectedPlan?.name ?: "Seleccionar plan", modifier = Modifier.fillMaxWidth())
                        }
                        DropdownMenu(expanded = planMenu, onDismissRequest = { planMenu = false }) {
                            orderPlans.forEach { p ->
                                DropdownMenuItem(text = { Text(p.name) }, onClick = {
                                    planId = p.id
                                    planMenu = false
                                    val start = orderDate.ifBlank { bulkFormTodayIsoDate() }
                                    endsAt = planEndsAtFromStart(start, p.billingPeriod).orEmpty()
                                })
                            }
                        }
                    }
                }
                Column(Modifier.weight(1f)) {
                    Text("Entrenador", color = AdminColors.TextMuted, fontSize = 12.sp)
                    Box {
                        OutlinedButton(
                            onClick = { trainerMenu = true },
                            modifier = Modifier.fillMaxWidth(),
                            shape = RoundedCornerShape(10.dp),
                        ) {
                            Text(
                                trainers.firstOrNull { it.id == trainerId }?.displayName ?: "Sin entrenador",
                                modifier = Modifier.fillMaxWidth(),
                            )
                        }
                        DropdownMenu(expanded = trainerMenu, onDismissRequest = { trainerMenu = false }) {
                            DropdownMenuItem(text = { Text("Sin entrenador") }, onClick = { trainerId = ""; trainerMenu = false })
                            trainers.forEach { t ->
                                DropdownMenuItem(text = { Text(t.displayName) }, onClick = { trainerId = t.id; trainerMenu = false })
                            }
                        }
                    }
                }
            }

            Spacer(Modifier.height(10.dp))
            // Fechas de pago, inicio y vencimiento: una fila en escritorio; apiladas en celular.
            val payDateField: @Composable (Modifier) -> Unit = { mod ->
                Column(mod) {
                    // Cortesía: no hay cobro, así que no se pide fecha de pago.
                    if (!isCourtesy) {
                        AdminDateField(
                            label = "Fecha de pago",
                            value = paymentDate.ifBlank { bulkFormTodayIsoDate() },
                            onValueChange = { paymentDate = it },
                            enabled = !saving,
                            useFloatingLabel = true,
                            placeholder = "AAAA-MM-DD",
                            outputFormat = AdminDateOutputFormat.ISO,
                        )
                        Text(
                            "Por defecto hoy; editable.",
                            color = AdminColors.TextMuted,
                            fontSize = 10.sp,
                            modifier = Modifier.padding(top = 2.dp, start = 2.dp),
                        )
                    }
                }
            }
            val startDateField: @Composable (Modifier) -> Unit = { mod ->
                Column(mod) {
                    AdminDateField(
                        label = "Fecha inicio",
                        value = startsIso,
                        onValueChange = { newStart ->
                            orderDate = newStart
                            val start = newStart.ifBlank { bulkFormTodayIsoDate() }
                            endsAt = selectedPlan?.let { planEndsAtFromStart(start, it.billingPeriod) }.orEmpty()
                        },
                        enabled = !saving,
                        useFloatingLabel = true,
                        placeholder = "AAAA-MM-DD",
                        outputFormat = AdminDateOutputFormat.ISO,
                    )
                    Text(
                        "Inicio de vigencia del plan.",
                        color = AdminColors.TextMuted,
                        fontSize = 10.sp,
                        modifier = Modifier.padding(top = 2.dp, start = 2.dp),
                    )
                }
            }
            // Vencimiento después del inicio. Cortesía: lo escribe quien la registra; en los demás se recalcula.
            val endDateField: @Composable (Modifier) -> Unit = { mod ->
                Column(mod) {
                    AdminDateField(
                        label = "Fecha de Vencimiento",
                        value = endsIso,
                        onValueChange = { endsAt = it },
                        enabled = !saving,
                        useFloatingLabel = true,
                        placeholder = "AAAA-MM-DD",
                        outputFormat = AdminDateOutputFormat.ISO,
                    )
                    Text(
                        "Se recalcula con inicio/plan; editable.",
                        color = AdminColors.TextMuted,
                        fontSize = 10.sp,
                        modifier = Modifier.padding(top = 2.dp, start = 2.dp),
                    )
                }
            }
            BoxWithConstraints(Modifier.fillMaxWidth()) {
                if (maxWidth < 600.dp) {
                    Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        payDateField(Modifier.fillMaxWidth())
                        startDateField(Modifier.fillMaxWidth())
                        endDateField(Modifier.fillMaxWidth())
                    }
                } else {
                    Row(
                        Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(10.dp),
                        verticalAlignment = Alignment.Top,
                    ) {
                        payDateField(Modifier.weight(1f))
                        startDateField(Modifier.weight(1f))
                        endDateField(Modifier.weight(1f))
                    }
                }
            }
            periodDuplicateWarning?.let { warn ->
                Spacer(Modifier.height(10.dp))
                Text(
                    warn,
                    color = AdminColors.Error,
                    fontSize = 13.sp,
                    fontWeight = FontWeight.Medium,
                )
            }

            if (!isCourtesy && selectedPlan != null) {
                Spacer(Modifier.height(10.dp))
                Column(
                    Modifier
                        .fillMaxWidth()
                        .clip(RoundedCornerShape(10.dp))
                        .background(AdminColors.Primary.copy(alpha = 0.06f))
                        .padding(12.dp),
                ) {
                    Text("Monto a pagar", fontWeight = FontWeight.SemiBold, color = AdminColors.Text, fontSize = 13.sp)
                    Spacer(Modifier.height(4.dp))
                    Text(
                        buildList {
                            add(formatMoney(selectedPlan.priceCents.toInt(), GymlyCurrency.CODE))
                            if (selectedPlan.priceUsdCents > 0) {
                                add(formatMoney(selectedPlan.priceUsdCents.toInt(), "USD"))
                            }
                        }.joinToString(" · "),
                        fontWeight = FontWeight.Bold,
                        color = AdminColors.Text,
                        fontSize = 16.sp,
                    )
                    if (creditAppliedCents > 0) {
                        Spacer(Modifier.height(4.dp))
                        Text(
                            "Saldo aplicado: ${formatMoney(creditAppliedCents.toInt(), GymlyCurrency.CODE)} · " +
                                "Por cobrar: ${formatMoney(amountDueCents.toInt(), GymlyCurrency.CODE)}",
                            color = AdminColors.Primary,
                            fontSize = 12.sp,
                        )
                    }
                }
                Spacer(Modifier.height(12.dp))
                CombinedPaymentLinesForm(
                    totalAmountInput = amountDueInput.ifBlank { planAmountInput },
                    defaultLineAmount = amountDueInput.ifBlank { planAmountInput },
                    defaultUsdAmount = planUsdInput,
                    enabled = !saving,
                    allowOverpay = true,
                    enforcePlanCoverage = false,
                    clientId = clientId.ifBlank { null },
                    membershipOrderId = initial?.id,
                    deferWalletApply = deferWalletApply,
                    onCreditApplied = { applied -> creditAppliedCents += applied },
                    onWalletBalanceLoaded = { walletBalanceCents = it },
                    onChangeGivenCentsChange = { changeGivenCents = it },
                    onChangeGivenUsdCentsChange = { changeGivenUsdCents = it },
                    onLinesChange = { paymentLines = it },
                    modifier = Modifier.fillMaxWidth(),
                ) { _, _, changeGiven, validate ->
                    changeGivenCents = changeGiven
                    validatePayment = { validate() }
                }
                // Descuento justo debajo del monto que se cobra (solo al crear, sin cortesía).
                if (initial == null && !isCourtesy) {
                    BulkDiscountSection(
                        bsInput = discountBsInput,
                        onBsInputChange = { discountBsInput = it },
                        usdInput = discountUsdInput,
                        onUsdInputChange = { discountUsdInput = it },
                        reason = discountReason,
                        onReasonChange = { discountReason = it },
                        note = discountNote,
                        onNoteChange = { discountNote = it },
                        enabled = !saving,
                    )
                    Spacer(Modifier.height(14.dp))
                }
                // Pendiente en $ y/o Bs (vacío en ambos = pago completo).
                Spacer(Modifier.height(14.dp))
                Text("Pendiente", fontWeight = FontWeight.SemiBold, color = AdminColors.Text, fontSize = 13.sp)
                Spacer(Modifier.height(4.dp))
                Text(
                    "Escribe el pendiente en Bs y en \$ (0,00 = pago completo). El pendiente puede ser en \$ o en Bs " +
                        "(aunque el método sea Divisas): indica en qué moneda queda la deuda.",
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                )
                Spacer(Modifier.height(6.dp))
                OutlinedTextField(
                    value = pendingBsInput,
                    onValueChange = { pendingBsInput = it },
                    enabled = !saving,
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    placeholder = { Text("Pendiente Bs") },
                    prefix = { Text("Bs ", color = AdminColors.TextMuted) },
                    shape = RoundedCornerShape(10.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = AdminColors.Primary,
                        unfocusedBorderColor = AdminColors.Outline,
                    ),
                )
                Spacer(Modifier.height(8.dp))
                OutlinedTextField(
                    value = pendingUsdInput,
                    onValueChange = { pendingUsdInput = it },
                    enabled = !saving,
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    placeholder = { Text("Pendiente \$") },
                    prefix = { Text("\$ ", color = AdminColors.TextMuted) },
                    shape = RoundedCornerShape(10.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = AdminColors.Primary,
                        unfocusedBorderColor = AdminColors.Outline,
                    ),
                )
            }

            (formError ?: serverError)?.let {
                Text(
                    it,
                    color = AdminColors.Error,
                    fontSize = 13.sp,
                    fontWeight = FontWeight.Medium,
                    modifier = Modifier.padding(top = 8.dp),
                )
            }

            Spacer(Modifier.height(14.dp))
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                TextButton(onClick = onDismiss, enabled = !saving) { Text("Cancelar") }
                Spacer(Modifier.width(8.dp))
                Button(
                    onClick = {
                        when {
                            clientId.isBlank() -> formError = "Selecciona un cliente de la lista."
                            planId.isBlank() -> formError = "Selecciona un plan."
                            !isCourtesy && (pendingBsInput.isBlank() || pendingUsdInput.isBlank()) ->
                                formError = "Escribe el pendiente en Bs y en \$ (0,00 si no queda saldo)."
                            !periodDuplicateWarning.isNullOrBlank() -> {
                                formError = periodDuplicateWarning
                            }
                            else -> {
                                val planCents = selectedPlan?.priceCents ?: 0L
                                val pendingUsd = parseBulkAmountToCents(pendingUsdInput)
                                val pendingBs = parseBulkAmountToCents(pendingBsInput)
                                if (!isCourtesy && planCents <= 0) {
                                    formError = "El plan seleccionado no tiene precio."
                                    return@Button
                                }
                                val parsedLines = when {
                                    isCourtesy -> emptyList()
                                    amountDueCents <= 0 && creditAppliedCents > 0 -> emptyList()
                                    else -> validatePayment?.invoke()
                                }
                                if (!isCourtesy && amountDueCents > 0 && parsedLines == null) {
                                    formError = "Revisa el desglose de cobro."
                                    return@Button
                                }
                                if (!isCourtesy && amountDueCents > 0 && parsedLines.isNullOrEmpty() && creditAppliedCents <= 0) {
                                    formError = "Indica al menos un método de pago."
                                    return@Button
                                }
                                val (discount, discountError) = bulkDiscountOrError(
                                    bsInput = if (isCourtesy || initial != null) "" else discountBsInput,
                                    reason = discountReason,
                                    note = discountNote,
                                    hasPending = pendingUsd > 0L || pendingBs > 0L,
                                    hasWalletCredit = creditAppliedCents > 0L,
                                    hasLines = !parsedLines.isNullOrEmpty(),
                                    usdInput = if (isCourtesy || initial != null) "" else discountUsdInput,
                                )
                                if (discountError != null) {
                                    formError = discountError
                                    return@Button
                                }
                                val primaryMethod = parsedLines?.firstOrNull()?.method
                                val resolvedOrderDate = parseFlexibleDateToIso(orderDate) ?: orderDate.take(10)
                                val resolvedPaymentDate = parseFlexibleDateToIso(paymentDate)?.take(10)
                                    ?: paymentDate.take(10).ifBlank { todayIsoDate() }
                                val submit = MembershipOrderFormSubmit(
                                    upsert = AdminMembershipOrderUpsert(
                                        clientId = clientId,
                                        planId = planId,
                                        branchId = companyId,
                                        trainerId = trainerId.ifBlank { null },
                                        method = if (isCourtesy) null else primaryMethod,
                                        payerPhone = null,
                                        status = when {
                                            isCourtesy -> AdminMembershipOrderStatus.COURTESY
                                            pendingUsd > 0L || pendingBs > 0L -> AdminMembershipOrderStatus.PENDING
                                            else -> AdminMembershipOrderStatus.PAID
                                        },
                                        orderDate = resolvedOrderDate,
                                        amountCents = if (isCourtesy) 0L else planCents,
                                        pendingUsdCents = if (isCourtesy) 0L else pendingUsd,
                                        pendingBsCents = if (isCourtesy) 0L else pendingBs,
                                        // Sin campo Notas en UI; se conserva al editar.
                                        notes = initial?.notes,
                                        // Cortesía: la fecha de vencimiento la escribe quien la registra.
                                        endsAt = if (isCourtesy) {
                                            parseFlexibleDateToIso(endsAt)?.take(10)
                                                ?: endsAt.take(10).takeIf { it.length == 10 }
                                        } else {
                                            null
                                        },
                                    ),
                                    paymentLines = parsedLines.orEmpty(),
                                    changeGivenCents = changeGivenCents,
                                    changeGivenUsdCents = changeGivenUsdCents,
                                    walletApplyCents = if (deferWalletApply) creditAppliedCents else 0L,
                                    remainingCollectCents = amountDueCents,
                                    paymentDate = resolvedPaymentDate,
                                    discount = discount,
                                )
                                // Mismo criterio que Ventas: en Bs no se cobra un excedente sin vuelto declarado.
                                // Solo se revisan las líneas en Bs; las divisas no se comparan.
                                val bsLinesCents = parsedLines.orEmpty()
                                    .filterNot { it.method.usesUsdAmount() }
                                    .sumOf { parseBulkAmountToCents(it.amountInput) }
                                val bsExcess = bsLinesCents - amountDueCents
                                if (!isCourtesy && bsExcess > 0L) {
                                    val undeclared = bsExcess - minOf(changeGivenCents, bsExcess)
                                    if (undeclared > maxOf(100L, amountDueCents / 200L)) {
                                        formError = "El cobro supera lo debido en Bs " +
                                            formatMoney(undeclared.toInt(), GymlyCurrency.CODE) +
                                            " y no tiene vuelto declarado. Corrige el monto o declara el vuelto."
                                        return@Button
                                    }
                                }
                                formError = null
                                formScope.launch {
                                    val covered = runCatching {
                                        AdminRepo.api.isMembershipPeriodCovered(
                                            clientId = clientId,
                                            planId = planId,
                                            orderDate = resolvedOrderDate.take(10),
                                            excludeOrderId = initial?.id,
                                        )
                                    }.getOrDefault(false)
                                    if (covered) {
                                        formError = selectedPlan?.let {
                                            duplicateMembershipPeriodMessage(it.name, it.billingPeriod)
                                        } ?: "Este socio ya tiene ese plan para el mismo periodo."
                                        return@launch
                                    }
                                    onSave(submit)
                                }
                            }
                        }
                    },
                    enabled = !saving &&
                        periodDuplicateWarning.isNullOrBlank() &&
                        (
                            isCourtesy ||
                                (
                                    (selectedPlan?.priceCents ?: 0L) > 0 &&
                                        clientId.isNotBlank() &&
                                        (
                                            walletBalanceCents > 0 ||
                                                creditAppliedCents > 0 ||
                                                amountDueCents > 0
                                            )
                                    )
                            ),
                    colors = ButtonDefaults.buttonColors(containerColor = AdminColors.Primary, contentColor = AdminColors.OnBrand),
                ) {
                    Text(
                        when {
                            saving -> "…"
                            isCourtesy -> "Guardar"
                            else -> "Cobrar"
                        },
                    )
                }
            }
        }
    }
}
